/**
 * ExtractionPreviewModal - Unified preview modal for all document extractions
 * TASK-214: Shows document content + all extracted structured data (KYC, Risk, Portfolio)
 *
 * Tabs:
 * - Document: Extracted text/tables from the document
 * - KYC Fields: All extracted KYC data (personalInfo, contactInfo, PEP, etc.)
 * - Risk Profile: All extracted risk profile data
 * - Portfolio: All extracted portfolio positions (if applicable)
 */

import React, { useState, useEffect } from 'react';
import { uploadService } from '../../services/uploadService';
import { httpClient } from '../../lib/httpClient';
import { CheckCircle, AlertCircle, FileText, User, Shield, TrendingUp, X } from 'lucide-react';
// TASK-232: Import FormDataMapper and prefill store
import { mapExtractionToAllForms } from '../../services/compliance';
import type { ExtractionResult } from '../../services/compliance';
import { useExtractionPrefillStore } from '../../stores/extractionPrefillStore';
// TASK-260: Import extraction transform utilities
import {
  wrapField,
  unwrapField,
  transformArray,
  transformWealthSource,
  transformIncomeSource,
  transformBusinessActivity,
} from '../../utils/extractionTransformers';

interface ExtractionPreviewModalProps {
  uploadId: string;
  stagingId?: string; // TASK-214: Staging ID for approval endpoint
  clientId?: string; // Add clientId to fetch real risk profile
  isOpen: boolean;
  onClose: () => void;
  onApprove: () => void;
  onApproveWithPrefill?: () => void; // TASK-232: Callback after mapping data for prefill
}

type TabType = 'document' | 'kyc' | 'risk' | 'portfolio' | 'transactions';

export const ExtractionPreviewModal: React.FC<ExtractionPreviewModalProps> = ({
  uploadId,
  stagingId,
  clientId,
  isOpen,
  onClose,
  onApprove,
  onApproveWithPrefill, // TASK-232
}) => {
  // TASK-232: Get prefill store action
  const setPendingExtraction = useExtractionPrefillStore((state) => state.setPendingExtraction);
  const [previewData, setPreviewData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('document');
  const [approving, setApproving] = useState(false);
  const [databaseRiskProfile, setDatabaseRiskProfile] = useState<any | null>(null);

  useEffect(() => {
    if (isOpen && uploadId) {
      fetchPreview();
      // Don't fetch database risk profile for CSV files - only for PDF KYC documents
      // Risk profile tab is hidden for CSV uploads
    }
  }, [isOpen, uploadId, clientId]);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('📥 Fetching preview for upload:', uploadId);

      const data = await uploadService.getPreview(uploadId);
      console.log('📦 Preview data received:', data);

      // TASK-214: Transform backend data structure if needed
      // Backend may return KYC data in sample_rows[0] instead of kyc_data
      const transformedData = transformPreviewData(data);
      console.log('📦 Transformed preview data:', transformedData);
      setPreviewData(transformedData);

      // Fetch database risk profile only for PDF KYC documents (not CSV files)
      const isCSVFile = transformedData.sample_rows && transformedData.sample_rows.length > 0;
      if (!isCSVFile && clientId) {
        fetchDatabaseRiskProfile();
      }

      // Auto-select most relevant tab based on what was extracted
      // TASK-225: Prioritize KYC/Risk Profile data over sample_rows for PDF documents
      const hasKycData = transformedData.kyc_data && Object.keys(transformedData.kyc_data).length > 0;
      const hasRiskProfileData = transformedData.risk_profile_data && Object.keys(transformedData.risk_profile_data).length > 0;
      const hasSampleRows = transformedData.sample_rows && transformedData.sample_rows.length > 0;
      const isPdfDocument = transformedData.page_extractions && transformedData.page_extractions.length > 0;

      // For PDF documents with KYC/Risk data, prioritize structured extraction
      if (hasKycData) {
        // PDF KYC: Show KYC tab (highest priority)
        console.log('📋 Auto-selecting KYC tab (kyc_data found)');
        setActiveTab('kyc');
      } else if (hasRiskProfileData || databaseRiskProfile) {
        // PDF with risk profile: Show risk tab
        console.log('📋 Auto-selecting Risk tab (risk_profile_data found)');
        setActiveTab('risk');
      } else if (hasSampleRows && !isPdfDocument) {
        // CSV/Excel files only: Detect if it's transactions or portfolio
        const dataType = detectDataType(transformedData.sample_rows);
        console.log('📋 Auto-selecting', dataType, 'tab (CSV/Excel data)');
        setActiveTab(dataType === 'transactions' ? 'transactions' : 'portfolio');
      } else if (transformedData.page_extractions && transformedData.page_extractions.length > 0) {
        // PDF without KYC: Show document tab
        console.log('📋 Auto-selecting Document tab (PDF without KYC)');
        setActiveTab('document');
      } else if (hasSampleRows) {
        // Fallback: sample_rows with unknown type
        const dataType = detectDataType(transformedData.sample_rows);
        setActiveTab(dataType === 'transactions' ? 'transactions' : 'portfolio');
      } else {
        // Final fallback
        setActiveTab('document');
      }
    } catch (err) {
      console.error('Failed to fetch preview:', err);
      setError('Failed to load extraction preview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Detect if sample data is portfolio positions or transactions
   */
  const detectDataType = (rows: any[]): 'portfolio' | 'transactions' => {
    if (!rows || rows.length === 0) return 'portfolio';

    const firstRow = rows[0];
    const transactionFields = ['transaction_date', 'trade_date', 'settlement_date', 'transaction_type', 'buy', 'sell'];
    const portfolioFields = ['market_value', 'book_value', 'unrealized_pl', 'total_value'];

    const hasTransactionFields = transactionFields.some(field => field in firstRow);
    const hasPortfolioFields = portfolioFields.some(field => field in firstRow);

    return hasTransactionFields && !hasPortfolioFields ? 'transactions' : 'portfolio';
  };

  /**
   * Fetch real risk profile from database
   */
  const fetchDatabaseRiskProfile = async () => {
    try {
      console.log('📥 Fetching database risk profile for client:', clientId);
      const response = await httpClient.get(`/api/risk-profile/${clientId}`);
      console.log('📦 Database risk profile received:', response);

      if (response.risk_profile && Object.keys(response.risk_profile).length > 0) {
        setDatabaseRiskProfile(response.risk_profile);
      }
    } catch (err) {
      console.error('Failed to fetch database risk profile:', err);
      // Don't show error - just use inferred profile if database fetch fails
    }
  };

  /**
   * Transform preview data from backend format to expected format
   *
   * Backend may return KYC fields in sample_rows[0] instead of kyc_data.
   * This function detects and transforms the data structure.
   */
  const transformPreviewData = (data: any): any => {
    // If kyc_data already exists, return as-is
    if (data.kyc_data) {
      return data;
    }

    // Check if sample_rows[0] contains KYC fields
    if (data.sample_rows && data.sample_rows.length > 0) {
      const firstRow = data.sample_rows[0];

      // Detect KYC fields: look for typical KYC field names
      const kycFieldIndicators = [
        'first_name', 'last_name', 'middle_name',
        'date_of_birth', 'email_primary', 'phone_primary',
        'residential_address', 'nationalities', 'marital_status',
        'is_pep', 'education', 'wealth_sources'
      ];

      const hasKYCFields = kycFieldIndicators.some(field => field in firstRow);

      // Detect portfolio fields
      const portfolioFieldIndicators = [
        'isin', 'security_name', 'quantity', 'market_value',
        'asset_class', 'book_value', 'unrealized_pl'
      ];

      const hasPortfolioFields = portfolioFieldIndicators.some(field => field in firstRow);

      // If it has KYC fields and not portfolio fields, transform to kyc_data structure
      if (hasKYCFields && !hasPortfolioFields) {
        console.log('🔄 Detected KYC data in sample_rows - transforming to kyc_data structure');

        // Transform flat structure to nested KYC structure - INCLUDE ALL FIELDS
        const kycData: any = {
          personalInfo: {
            firstName: firstRow.first_name,
            lastName: firstRow.last_name,
            middleName: firstRow.middle_name,
            dateOfBirth: firstRow.date_of_birth,
            nationality: firstRow.nationalities?.[0] || firstRow.nationality,
            nationalities: firstRow.nationalities, // All nationalities
            gender: firstRow.gender,
            maritalStatus: firstRow.marital_status,
            education: firstRow.education,
            educationDetails: firstRow.education_details,
            interests: firstRow.interests
          },
          contactInfo: {
            emailPrimary: firstRow.email_primary,
            phonePrimary: firstRow.phone_primary,
            residentialAddress: firstRow.residential_address
          },
          employmentInfo: {
            employmentStatus: firstRow.employment_status,
            occupation: firstRow.occupation,
            employerName: firstRow.employer_name,
            industry: firstRow.employment_industry || firstRow.business_industries?.[0],
            businessIndustries: firstRow.business_industries,
            businessCountries: firstRow.business_countries
          },
          taxInfo: {
            taxResidence: firstRow.tax_residence || firstRow.residential_address?.country,
            tinNumber: firstRow.tin_number,
            usPerson: firstRow.us_person || false
          },
          pepStatus: {
            isPEP: firstRow.is_pep || false,
            isRCA: firstRow.is_rca || false,
            classification: firstRow.pep_classification,
            hasAdverseMedia: firstRow.has_adverse_media
          },
          wealthSources: firstRow.wealth_sources || [],
          // Additional fields that don't fit standard categories
          additionalInfo: {
            totalAssets: firstRow.total_assets,
            liquidAssets: firstRow.liquid_assets,
            insiderStatus: firstRow.insider_status,
            insiderSecurities: firstRow.insider_securities
          }
        };

        // Remove null/undefined values
        const cleanKYCData = removeEmptyValues(kycData);

        // ALSO check if we can extract risk profile data from the same document
        const riskProfileData = extractRiskProfileData(firstRow);

        console.log('🔍 KYC transformation:', {
          original: firstRow,
          transformed: cleanKYCData,
          removedFields: Object.keys(firstRow).filter(k => !JSON.stringify(cleanKYCData).includes(k))
        });

        return {
          ...data,
          kyc_data: cleanKYCData,
          risk_profile_data: riskProfileData, // Add risk profile if detected
          _original_sample_rows: data.sample_rows, // Keep original data for debugging
          sample_rows: [] // Clear sample_rows since we moved it to kyc_data
        };
      }
    }

    // No transformation needed
    return data;
  };

  /**
   * Extract risk profile data from document fields
   * Maps document fields to risk profile concepts
   */
  const extractRiskProfileData = (row: any): any => {
    const riskProfile: any = {};

    // Map education level to financial knowledge
    if (row.education) {
      const educationMap: Record<string, string> = {
        'university': 'Advanced',
        'doctorate': 'Expert',
        'masters': 'Advanced',
        'bachelors': 'Intermediate',
        'high_school': 'Basic',
        'professional': 'Intermediate'
      };
      riskProfile.financialKnowledge = educationMap[row.education.toLowerCase()] || row.education;
    }

    // Map occupation/business to investment experience
    if (row.occupation) {
      const occupationLower = row.occupation.toLowerCase();
      if (occupationLower.includes('investment') || occupationLower.includes('finance') ||
          occupationLower.includes('capital') || occupationLower.includes('portfolio')) {
        riskProfile.investmentExperience = 'Extensive - Professional background in finance/investments';
      } else if (occupationLower.includes('business') || occupationLower.includes('entrepreneur')) {
        riskProfile.investmentExperience = 'Significant - Business owner/executive';
      } else {
        riskProfile.investmentExperience = 'Inferred from occupation: ' + row.occupation.substring(0, 100);
      }
    }

    // Map total assets to risk capacity
    if (row.total_assets || row.liquid_assets) {
      const totalAssets = row.total_assets || 0;
      const liquidAssets = row.liquid_assets || 0;

      if (totalAssets > 1000000000) { // > 1B
        riskProfile.riskCapacity = 'Very High - Ultra High Net Worth';
      } else if (totalAssets > 100000000) { // > 100M
        riskProfile.riskCapacity = 'High - High Net Worth';
      } else if (totalAssets > 10000000) { // > 10M
        riskProfile.riskCapacity = 'Medium-High - Affluent';
      } else {
        riskProfile.riskCapacity = 'Medium';
      }

      riskProfile.liquidityPosition = liquidAssets > 0
        ? `CHF ${liquidAssets.toLocaleString()} in liquid assets`
        : 'Not specified';
    }

    // Map interests to investment objectives
    if (row.interests && Array.isArray(row.interests)) {
      const interestBased = row.interests.map((i: string) => i.charAt(0).toUpperCase() + i.slice(1)).join(', ');
      riskProfile.personalInterests = interestBased;
      // Could infer objectives from lifestyle interests
      if (row.interests.includes('sports') || row.interests.includes('travelling')) {
        riskProfile.lifestyleGoals = 'Active lifestyle with travel and leisure interests';
      }
    }

    // Map wealth sources to investment objectives
    if (row.wealth_sources && Array.isArray(row.wealth_sources)) {
      const sources = row.wealth_sources.map((s: any) => s.sourceType).join(', ');
      riskProfile.wealthOrigin = sources;

      // Infer risk tolerance from wealth source diversity
      if (row.wealth_sources.length > 2) {
        riskProfile.portfolioDiversification = 'Highly diversified wealth sources indicate preference for risk diversification';
      }
    }

    // Map business industries to sector preferences
    if (row.business_industries && Array.isArray(row.business_industries)) {
      riskProfile.industryExpertise = row.business_industries.join(', ');
      riskProfile.sectorKnowledge = `Deep knowledge in: ${row.business_industries.join(', ')}`;
    }

    // Map age to investment horizon
    if (row.date_of_birth) {
      const birthYear = new Date(row.date_of_birth).getFullYear();
      const age = new Date().getFullYear() - birthYear;

      if (age > 70) {
        riskProfile.investmentHorizon = 'Short-term (5-10 years) - Age ' + age;
      } else if (age > 55) {
        riskProfile.investmentHorizon = 'Medium-term (10-15 years) - Age ' + age;
      } else if (age > 40) {
        riskProfile.investmentHorizon = 'Long-term (15-20 years) - Age ' + age;
      } else {
        riskProfile.investmentHorizon = 'Very long-term (20+ years) - Age ' + age;
      }
    }

    // Add metadata
    if (Object.keys(riskProfile).length > 0) {
      riskProfile._inference = {
        method: 'Inferred from KYC document data',
        confidence: 'Medium - Derived from related fields',
        note: 'This risk profile is automatically inferred. Formal risk assessment recommended.'
      };
    }

    return Object.keys(riskProfile).length > 0 ? riskProfile : null;
  };

  /**
   * Recursively remove null, undefined, and empty objects/arrays
   * BUT keep empty strings, zeros, and false values (they're valid)
   */
  const removeEmptyValues = (obj: any): any => {
    // null and undefined are truly empty
    if (obj === null || obj === undefined) return undefined;

    // Empty string, 0, false are VALID values - keep them
    if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
      return obj;
    }

    if (Array.isArray(obj)) {
      const filtered = obj.filter(item => item !== null && item !== undefined);
      return filtered.length > 0 ? filtered : undefined;
    }

    if (typeof obj === 'object') {
      const result: any = {};
      for (const key in obj) {
        const value = removeEmptyValues(obj[key]);
        // Keep the value if it's not undefined (includes '', 0, false, etc.)
        if (value !== undefined) {
          result[key] = value;
        }
      }
      return Object.keys(result).length > 0 ? result : undefined;
    }

    return obj;
  };

  const handleApprove = async () => {
    try {
      setApproving(true);

      // TASK-232: Transform preview data to ExtractionResult and map to all forms
      const hasKycOrRiskData = previewData?.kyc_data || previewData?.risk_profile_data;

      if (hasKycOrRiskData && clientId) {
        console.log('📋 TASK-232: Mapping extraction to all compliance forms');

        try {
          const extractionResult = transformPreviewToExtractionResult(previewData, uploadId);
          const { compliance, forms } = mapExtractionToAllForms(extractionResult);

          console.log('📋 Mapped compliance data:', {
            personal: Object.keys(compliance.personal || {}).length,
            contact: Object.keys(compliance.contact || {}).length,
            employment: Object.keys(compliance.employment || {}).length,
            financial: Object.keys(compliance.financial || {}).length,
          });

          // Store in Zustand for ComplianceTab to consume
          // TASK-232: Include raw KYC/Risk data for more complete ingestion
          setPendingExtraction({
            clientId,
            extractionData: extractionResult,
            mappedForms: forms,
            mappedCompliance: compliance,
            sourceName: previewData?.filename || 'Document',
            approvedAt: new Date().toISOString(),
            rawKycData: previewData?.kyc_data,
            rawRiskData: previewData?.risk_profile_data,
          });

          console.log('✅ TASK-232: Pending extraction stored for client:', clientId);
        } catch (mapError) {
          console.error('⚠️ TASK-232: Form mapping failed, continuing with approval:', mapError);
          // Don't block approval if mapping fails
        }
      }

      // TASK-214: Use staging approval endpoint if stagingId is provided
      if (stagingId) {
        console.log('✅ Approving staging extraction:', stagingId);
        await uploadService.approveStagingExtraction(stagingId);
      } else {
        console.log('✅ Confirming upload:', uploadId);

        // Check if this is a transaction document that needs special handling
        const hasTransactions = previewData?.sample_rows && previewData.sample_rows.length > 0 &&
          detectDataType(previewData.sample_rows) === 'transactions';

        if (hasTransactions && clientId) {
          console.log('📊 Detected transaction document - triggering transaction extraction');
          // Call the dedicated transaction extraction endpoint with FormData
          const formData = new FormData();
          formData.append('upload_id', uploadId);
          formData.append('client_id', clientId);

          const rawClient = httpClient.getRawClient();
          await rawClient.post('/portfolio/transactions/extract-from-upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } else {
          // Regular confirm upload for other document types
          await uploadService.confirmUpload(uploadId);
        }
      }

      // TASK-232: Trigger prefill navigation if callback provided and we have KYC/Risk data
      if (onApproveWithPrefill && hasKycOrRiskData && clientId) {
        console.log('🚀 TASK-232: Triggering navigation to Compliance tab');
        onApproveWithPrefill();
      }

      onApprove();
    } catch (err) {
      console.error('Failed to approve:', err);
      setError('Failed to approve extraction. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  /**
   * TASK-232/TASK-260: Transform modal's preview data to ExtractionResult format
   * for FormDataMapper consumption
   *
   * IMPORTANT: Backend previewData uses {value, confidence} wrappers around fields.
   * This function unwraps those and re-wraps them in the ExtractionResult format.
   *
   * TASK-260: Expanded to map ALL 15 backend sections (100+ fields)
   */
  const transformPreviewToExtractionResult = (data: any, uploadId: string): ExtractionResult => {
    const kycData = data?.kyc_data || {};
    const riskData = data?.risk_profile_data || {};

    // Get base confidence from preview data or default
    const baseConfidence = data?.extraction_confidence || 0.8;

    // Extract all backend sections
    const personalInfo = kycData.personalInfo || {};
    const contactInfo = kycData.contactInfo || {};
    const address = kycData.address || {};
    const identification = kycData.identification || {};
    const employmentInfo = kycData.employmentInfo || {};
    const taxInfo = kycData.taxInfo || {};
    const pepStatus = kycData.pepStatus || {};
    const personalBackground = kycData.personalBackground || {};
    const wealthSummary = kycData.wealthSummary || {};
    const assetBreakdown = kycData.assetBreakdown || {};
    const additionalWealthInfo = kycData.additionalWealthInfo || {};
    const incomeExpectations = kycData.incomeExpectations || {};

    // Count extracted fields for metadata
    let fieldsExtracted = 0;
    const countField = (field: any) => {
      if (unwrapField(field) !== undefined) fieldsExtracted++;
    };

    // Count key fields
    countField(personalInfo.firstName);
    countField(personalInfo.lastName);
    countField(personalInfo.dateOfBirth);
    countField(contactInfo.phonePrimary || contactInfo.phone);
    countField(contactInfo.emailPrimary || contactInfo.email);
    countField(employmentInfo.occupation);
    countField(wealthSummary.netWorth);
    countField(identification.passportNumber);

    return {
      success: true,
      upload_id: uploadId,
      extraction_timestamp: new Date().toISOString(),
      confidence: baseConfidence,
      fields_extracted: fieldsExtracted,
      fields_total: 100,
      client_data: {
        // 1. Personal Info (12 fields)
        personal_info: {
          first_name: wrapField(personalInfo.firstName, baseConfidence),
          middle_name: wrapField(personalInfo.middleName, baseConfidence),
          last_name: wrapField(personalInfo.lastName, baseConfidence),
          date_of_birth: wrapField(personalInfo.dateOfBirth, baseConfidence),
          place_of_birth: wrapField(personalInfo.placeOfBirth, baseConfidence * 0.9),
          country_of_birth: wrapField(personalInfo.countryOfBirth, baseConfidence * 0.9),
          gender: wrapField(personalInfo.gender, baseConfidence),
          marital_status: wrapField(personalInfo.maritalStatus, baseConfidence * 0.9),
          nationalities: wrapField(personalInfo.nationalities, baseConfidence),
          languages: wrapField(personalInfo.languages, baseConfidence * 0.8),
          passport_number: wrapField(personalInfo.passportNumber || identification.passportNumber, baseConfidence),
          national_id: wrapField(personalInfo.nationalId, baseConfidence),
          ssn: wrapField(personalInfo.ssn || identification.ahvNumber, baseConfidence),
          former_names: wrapField(personalInfo.formerNames, baseConfidence * 0.8),
        },

        // 2. Contact Info (9 fields)
        contact_info: {
          phone: wrapField(contactInfo.phonePrimary || contactInfo.phone, baseConfidence),
          phone_secondary: wrapField(contactInfo.phoneSecondary, baseConfidence * 0.9),
          mobile_phone: wrapField(contactInfo.mobilePhone, baseConfidence),
          email: wrapField(contactInfo.emailPrimary || contactInfo.email, baseConfidence),
          email_secondary: wrapField(contactInfo.emailSecondary, baseConfidence * 0.9),
          address: wrapField(contactInfo.residentialAddress || address, baseConfidence * 0.85),
          mailing_address: wrapField(contactInfo.mailingAddress, baseConfidence * 0.85),
          preferred_contact_method: wrapField(contactInfo.preferredContactMethod, baseConfidence * 0.7),
          relationship_reason: wrapField(contactInfo.relationshipReason, baseConfidence * 0.75),
          relationship_purpose: wrapField(contactInfo.relationshipPurpose, baseConfidence * 0.75),
          previous_addresses: wrapField(contactInfo.previousAddresses, baseConfidence * 0.7),
        },

        // 3. Address (7 fields) - standalone section
        address: {
          street: wrapField(address.street, baseConfidence * 0.9),
          street_address_2: wrapField(address.streetAddress2, baseConfidence * 0.85),
          city: wrapField(address.city, baseConfidence * 0.9),
          state_province: wrapField(address.stateProvince, baseConfidence * 0.85),
          canton: wrapField(address.canton, baseConfidence * 0.85),
          postal_code: wrapField(address.postalCode, baseConfidence * 0.9),
          country: wrapField(address.country, baseConfidence * 0.9),
        },

        // 4. Identification (5 fields)
        identification: {
          passport_number: wrapField(identification.passportNumber, baseConfidence),
          passport_expiry: wrapField(identification.passportExpiry, baseConfidence * 0.9),
          passport_country: wrapField(identification.passportCountry, baseConfidence * 0.9),
          id_card_number: wrapField(identification.idCardNumber, baseConfidence),
          ahv_number: wrapField(identification.ahvNumber, baseConfidence),
        },

        // 5. Employment Info (11 fields)
        employment_info: {
          status: wrapField(employmentInfo.employmentStatus, baseConfidence),
          occupation: wrapField(employmentInfo.occupation, baseConfidence),
          employer: wrapField(employmentInfo.employer || employmentInfo.employerName, baseConfidence),
          industry: wrapField(employmentInfo.industry || employmentInfo.employmentIndustry, baseConfidence * 0.9),
          years_employed: wrapField(employmentInfo.yearsEmployed, baseConfidence * 0.8),
          annual_income: wrapField(employmentInfo.annualIncome, baseConfidence * 0.85),
          income_currency: wrapField(employmentInfo.incomeCurrency, baseConfidence * 0.85),
          work_address: wrapField(
            employmentInfo.workAddress || {
              street: unwrapField(employmentInfo.workAddressStreet),
              city: unwrapField(employmentInfo.workAddressCity),
              postal_code: unwrapField(employmentInfo.workAddressPostalCode),
              country: unwrapField(employmentInfo.workAddressCountry),
            },
            baseConfidence * 0.8
          ),
        },

        // 6. Financial Info (from wealthSummary + assetBreakdown)
        financial_info: {
          total_assets: wrapField(
            wealthSummary.netWorth || riskData.section3?.assets?.total_assets,
            baseConfidence * 0.85
          ),
          liquid_assets: wrapField(
            wealthSummary.liquidNetWorth || riskData.section3?.assets?.cash_deposits,
            baseConfidence * 0.85
          ),
          annual_income: wrapField(
            employmentInfo.annualIncome || riskData.section3?.income?.total_annual_net,
            baseConfidence * 0.85
          ),
          net_worth: wrapField(wealthSummary.netWorth, baseConfidence * 0.8),
          net_worth_currency: wrapField(wealthSummary.netWorthCurrency, baseConfidence * 0.85),
          source_of_wealth: wrapField(
            wealthSummary.sourceOfWealth || wealthSummary.primaryWealthSource,
            baseConfidence * 0.75
          ),
          source_of_funds: wrapField(wealthSummary.sourceOfFunds, baseConfidence * 0.75),
          wealth_narrative: wrapField(wealthSummary.wealthNarrative, baseConfidence * 0.7),
          securities: wrapField(
            assetBreakdown.assetBreakdownSecurities && unwrapField(wealthSummary.netWorth)
              ? (unwrapField(wealthSummary.netWorth) as number) * ((unwrapField(assetBreakdown.assetBreakdownSecurities) as number) / 100)
              : undefined,
            baseConfidence * 0.7
          ),
          real_estate: wrapField(
            assetBreakdown.assetBreakdownRealEstate && unwrapField(wealthSummary.netWorth)
              ? (unwrapField(wealthSummary.netWorth) as number) * ((unwrapField(assetBreakdown.assetBreakdownRealEstate) as number) / 100)
              : undefined,
            baseConfidence * 0.7
          ),
        },

        // 7. Tax Info (4 fields)
        tax_info: {
          residence: wrapField(taxInfo.taxResidency || taxInfo.taxResidence, baseConfidence),
          tax_id: wrapField(taxInfo.taxIdNumber || taxInfo.taxId, baseConfidence),
          us_person: wrapField(taxInfo.usPersonStatus ?? taxInfo.usPerson, baseConfidence),
          fatca_status: wrapField(taxInfo.fatcaStatus, baseConfidence * 0.9),
          tax_jurisdictions: wrapField(taxInfo.taxJurisdictions, baseConfidence * 0.85),
        },

        // 8. PEP Info (19 fields)
        pep_info: {
          is_pep: wrapField(pepStatus.isPEP ?? pepStatus.isPep, baseConfidence),
          is_rca: wrapField(pepStatus.isRCA ?? pepStatus.isRca, baseConfidence),
          pep_classification: wrapField(pepStatus.pepClassification, baseConfidence * 0.9),
          pep_type: wrapField(pepStatus.pepType, baseConfidence * 0.9),
          pep_role: wrapField(pepStatus.pepRole, baseConfidence * 0.85),
          pep_relation: wrapField(pepStatus.pepRelation, baseConfidence * 0.85),
          pep_position: wrapField(
            pepStatus.pepRole ||
            (Array.isArray(unwrapField(pepStatus.pepPositions))
              ? (unwrapField(pepStatus.pepPositions) as string[])?.[0]
              : pepStatus.pepPositions?.[0]),
            baseConfidence * 0.85
          ),
          pep_positions: wrapField(pepStatus.pepPositions, baseConfidence * 0.85),
          pep_country: wrapField(
            (Array.isArray(unwrapField(pepStatus.pepCountries))
              ? (unwrapField(pepStatus.pepCountries) as string[])?.[0]
              : pepStatus.pepCountries?.[0]),
            baseConfidence * 0.85
          ),
          pep_countries: wrapField(pepStatus.pepCountries, baseConfidence * 0.85),
          pep_start_date: wrapField(pepStatus.pepStartDate, baseConfidence * 0.85),
          pep_end_date: wrapField(pepStatus.pepEndDate, baseConfidence * 0.85),
          pep_years_in_position: wrapField(pepStatus.pepYearsInPosition, baseConfidence * 0.8),
          pep_years_since_exit: wrapField(pepStatus.pepYearsSinceExit, baseConfidence * 0.8),
          pep_risk_level: wrapField(pepStatus.pepRiskLevel, baseConfidence * 0.85),
          enhanced_due_diligence: wrapField(pepStatus.enhancedDueDiligence, baseConfidence * 0.9),
          monitoring_frequency: wrapField(pepStatus.monitoringFrequency, baseConfidence * 0.85),
          rca_relationship: wrapField(pepStatus.rcaRelationship || pepStatus.pepRelation, baseConfidence * 0.85),
          rca_pep_name: wrapField(pepStatus.rcaPepName, baseConfidence * 0.85),
          is_insider: wrapField(pepStatus.isInsider, baseConfidence),
          insider_securities: wrapField(pepStatus.insiderSecurities, baseConfidence * 0.85),
          insider_company_names: wrapField(pepStatus.insiderCompanyNames, baseConfidence * 0.85),
        },

        // 9. Personal Background (7 fields)
        personal_background: {
          education_level: wrapField(personalBackground.educationLevel, baseConfidence * 0.8),
          institutions_attended: wrapField(personalBackground.institutionsAttended, baseConfidence * 0.75),
          professional_certifications: wrapField(personalBackground.professionalCertifications, baseConfidence * 0.75),
          hobbies_interests: wrapField(personalBackground.hobbiesInterests, baseConfidence * 0.7),
          family_composition: wrapField(personalBackground.familyComposition, baseConfidence * 0.75),
          dependents_count: wrapField(personalBackground.dependentsCount, baseConfidence * 0.8),
          spouse_employment_status: wrapField(personalBackground.spouseEmploymentStatus, baseConfidence * 0.75),
        },

        // 10. Wealth Summary (7 fields)
        wealth_summary: {
          net_worth: wrapField(wealthSummary.netWorth, baseConfidence * 0.85),
          liquid_net_worth: wrapField(wealthSummary.liquidNetWorth, baseConfidence * 0.85),
          net_worth_currency: wrapField(wealthSummary.netWorthCurrency, baseConfidence * 0.9),
          source_of_wealth: wrapField(wealthSummary.sourceOfWealth, baseConfidence * 0.8),
          source_of_funds: wrapField(wealthSummary.sourceOfFunds, baseConfidence * 0.8),
          primary_wealth_source: wrapField(wealthSummary.primaryWealthSource, baseConfidence * 0.8),
          wealth_narrative: wrapField(wealthSummary.wealthNarrative, baseConfidence * 0.75),
        },

        // 11. Asset Breakdown (7 fields)
        asset_breakdown: {
          cash: wrapField(assetBreakdown.assetBreakdownCash, baseConfidence * 0.8),
          securities: wrapField(assetBreakdown.assetBreakdownSecurities, baseConfidence * 0.8),
          real_estate: wrapField(assetBreakdown.assetBreakdownRealEstate, baseConfidence * 0.8),
          business_equity: wrapField(assetBreakdown.assetBreakdownBusinessEquity, baseConfidence * 0.8),
          commodities: wrapField(assetBreakdown.assetBreakdownCommodities, baseConfidence * 0.75),
          crypto: wrapField(assetBreakdown.assetBreakdownCrypto, baseConfidence * 0.75),
          other: wrapField(assetBreakdown.assetBreakdownOther, baseConfidence * 0.75),
        },

        // 12. Additional Wealth Info (5 fields)
        additional_wealth_info: {
          inheritance_expected: wrapField(additionalWealthInfo.inheritanceExpected, baseConfidence * 0.8),
          inheritance_details: wrapField(additionalWealthInfo.inheritanceDetails, baseConfidence * 0.75),
          unusual_transactions: wrapField(additionalWealthInfo.unusualTransactions, baseConfidence * 0.85),
          unusual_transaction_details: wrapField(additionalWealthInfo.unusualTransactionDetails, baseConfidence * 0.75),
          source_documentation: wrapField(additionalWealthInfo.sourceDocumentation, baseConfidence * 0.8),
        },

        // 13. Income Expectations (6 fields)
        income_expectations: {
          expected_income_change: wrapField(incomeExpectations.expectedIncomeChange, baseConfidence * 0.8),
          expected_change_details: wrapField(incomeExpectations.expectedChangeDetails, baseConfidence * 0.75),
          retirement_income: wrapField(incomeExpectations.retirementIncome, baseConfidence * 0.8),
          retirement_income_details: wrapField(incomeExpectations.retirementIncomeDetails, baseConfidence * 0.75),
          other_financial_support: wrapField(incomeExpectations.otherFinancialSupport, baseConfidence * 0.8),
          other_support_details: wrapField(incomeExpectations.otherSupportDetails, baseConfidence * 0.75),
        },

        // 14. Wealth Sources (array - 20 fields per item)
        wealth_sources: transformArray(
          kycData.wealthSources,
          transformWealthSource,
          baseConfidence
        ) as any,

        // 15. Income Sources (array - 16 fields per item)
        income_sources: transformArray(
          kycData.incomeSources,
          transformIncomeSource,
          baseConfidence
        ) as any,

        // 16. Business Activities (array - 19 fields per item)
        business_activities: transformArray(
          kycData.businessActivities,
          transformBusinessActivity,
          baseConfidence
        ) as any,
      },
    };
  };

  if (!isOpen) return null;

  // Helper to render field with confidence - TASK-225: Handle {value, confidence} wrapper and currency objects
  const renderField = (label: string, value: any, confidence?: number) => {
    if (value === null || value === undefined || value === '') return null;

    let displayValue = value;

    // Unwrap {value, confidence} structure
    if (typeof value === 'object' && value.value !== undefined) {
      displayValue = value.value;
      confidence = value.confidence;
    }

    // Format currency objects {amount, currency}
    if (typeof displayValue === 'object' && displayValue !== null) {
      if (displayValue.amount !== undefined) {
        // Currency object
        displayValue = `${displayValue.currency || 'CHF'} ${displayValue.amount.toLocaleString()}`;
      } else if (Array.isArray(displayValue)) {
        // Array - join with commas
        displayValue = displayValue.join(', ');
      } else {
        // Other objects - stringify
        displayValue = JSON.stringify(displayValue, null, 2);
      }
    }

    return (
      <div key={label} className="flex justify-between items-start py-2 border-b border-gray-100">
        <dt className="text-sm font-medium text-gray-600 w-1/3">{label}</dt>
        <dd className="text-sm text-gray-900 w-2/3 text-right flex items-center justify-end gap-2">
          <span className="whitespace-pre-wrap">{String(displayValue)}</span>
          {confidence !== undefined && confidence > 0 && (
            <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${
              confidence > 0.8 ? 'bg-green-100 text-green-700' :
              confidence > 0.6 ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              {(confidence * 100).toFixed(0)}%
            </span>
          )}
        </dd>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">
              📋 Extraction Review
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {previewData?.filename || 'Document'} • Review all extracted data
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {/* Only show Document Content tab for PDFs (files with page_extractions) */}
          {previewData?.page_extractions && previewData.page_extractions.length > 0 && (
            <button
              onClick={() => setActiveTab('document')}
              className={`px-6 py-3 font-medium text-sm border-b-2 whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'document'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="w-4 h-4" />
              Document Content
            </button>
          )}

          {previewData?.kyc_data && (
            <button
              onClick={() => setActiveTab('kyc')}
              className={`px-6 py-3 font-medium text-sm border-b-2 whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'kyc'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <User className="w-4 h-4" />
              KYC Fields
              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded">
                Extracted
              </span>
            </button>
          )}

          {/* TASK-225: Show Risk Profile tab when risk_profile_data exists (regardless of sample_rows) */}
          {(previewData?.risk_profile_data || databaseRiskProfile) && (
            <button
              onClick={() => setActiveTab('risk')}
              className={`px-6 py-3 font-medium text-sm border-b-2 whitespace-nowrap flex items-center gap-2 ${
                activeTab === 'risk'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Shield className="w-4 h-4" />
              Risk Profile
            </button>
          )}

          {previewData?.sample_rows && previewData.sample_rows.length > 0 && (() => {
            const dataType = detectDataType(previewData.sample_rows);
            if (dataType === 'transactions') {
              return (
                <button
                  onClick={() => setActiveTab('transactions')}
                  className={`px-6 py-3 font-medium text-sm border-b-2 whitespace-nowrap flex items-center gap-2 ${
                    activeTab === 'transactions'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <TrendingUp className="w-4 h-4" />
                  Transactions ({previewData.total_rows || previewData.sample_rows.length})
                </button>
              );
            } else {
              return (
                <button
                  onClick={() => setActiveTab('portfolio')}
                  className={`px-6 py-3 font-medium text-sm border-b-2 whitespace-nowrap flex items-center gap-2 ${
                    activeTab === 'portfolio'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <TrendingUp className="w-4 h-4" />
                  Portfolio ({previewData.total_rows || previewData.sample_rows.length} positions)
                </button>
              );
            }
          })()}
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600 mb-4"></div>
                <div className="text-gray-600">Loading extraction data...</div>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-600" />
                <div className="text-red-600 mb-4">{error}</div>
                <button
                  onClick={fetchPreview}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : previewData ? (
            <div className="h-full overflow-y-auto">
              {/* Document Content Tab */}
              {activeTab === 'document' && (
                <div className="p-6 space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Extracted Document Content</h3>

                  {previewData.page_extractions?.map((page: any, index: number) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Page {page.page_number}</h4>
                      {page.raw_text ? (
                        <pre className="whitespace-pre-wrap text-sm font-mono text-gray-600 bg-gray-50 p-3 rounded">
                          {page.raw_text}
                        </pre>
                      ) : (
                        <div className="text-gray-500 italic">No text extracted</div>
                      )}
                    </div>
                  ))}

                  {(!previewData.page_extractions || previewData.page_extractions.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      No document content available
                    </div>
                  )}
                </div>
              )}

              {/* KYC Fields Tab - TASK-225: All sections matching kyc-service/models/requests.py */}
              {activeTab === 'kyc' && previewData.kyc_data && (
                <div className="p-6 space-y-4 overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Extracted KYC Fields</h3>
                    {previewData.extraction_confidence && (
                      <span className="text-sm text-gray-600">
                        Overall Confidence: <span className="font-medium text-green-600">
                          {(previewData.extraction_confidence * 100).toFixed(0)}%
                        </span>
                      </span>
                    )}
                  </div>

                  {/* 1. Personal Information (PersonalInfoCreate) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      1. Personal Information
                    </h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {renderField('First Name', previewData.kyc_data.personalInfo?.firstName)}
                      {renderField('Middle Name', previewData.kyc_data.personalInfo?.middleName)}
                      {renderField('Last Name', previewData.kyc_data.personalInfo?.lastName)}
                      {renderField('Date of Birth', previewData.kyc_data.personalInfo?.dateOfBirth)}
                      {renderField('Place of Birth', previewData.kyc_data.personalInfo?.placeOfBirth)}
                      {renderField('Gender', previewData.kyc_data.personalInfo?.gender)}
                      {renderField('Marital Status', previewData.kyc_data.personalInfo?.maritalStatus)}
                      {renderField('Nationalities', previewData.kyc_data.personalInfo?.nationalities)}
                      {renderField('Passport Number', previewData.kyc_data.personalInfo?.passportNumber)}
                      {renderField('National ID', previewData.kyc_data.personalInfo?.nationalId)}
                      {renderField('SSN', previewData.kyc_data.personalInfo?.ssn)}
                      {renderField('Languages', previewData.kyc_data.personalInfo?.languages)}
                      {renderField('Former Names', previewData.kyc_data.personalInfo?.formerNames)}
                    </dl>
                  </div>

                  {/* 2. Contact Information (ContactInfoCreate) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">2. Contact Information</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {renderField('Primary Phone', previewData.kyc_data.contactInfo?.phonePrimary)}
                      {renderField('Secondary Phone', previewData.kyc_data.contactInfo?.phoneSecondary)}
                      {renderField('Primary Email', previewData.kyc_data.contactInfo?.emailPrimary)}
                      {renderField('Secondary Email', previewData.kyc_data.contactInfo?.emailSecondary)}
                      {renderField('Preferred Contact', previewData.kyc_data.contactInfo?.preferredContactMethod)}
                      {renderField('Relationship Reason', previewData.kyc_data.contactInfo?.relationshipReason)}
                      {renderField('Relationship Purpose', previewData.kyc_data.contactInfo?.relationshipPurpose)}
                    </dl>
                  </div>

                  {/* 3. Residential Address (AddressCreate) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">3. Residential Address</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {renderField('Street Address', previewData.kyc_data.address?.streetAddress)}
                      {renderField('Street Address 2', previewData.kyc_data.address?.streetAddress2)}
                      {renderField('City', previewData.kyc_data.address?.city)}
                      {renderField('State/Province', previewData.kyc_data.address?.stateProvince)}
                      {renderField('Postal Code', previewData.kyc_data.address?.postalCode)}
                      {renderField('Country', previewData.kyc_data.address?.country)}
                    </dl>
                  </div>

                  {/* 4. Employment Information (EmploymentInfoCreate) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">4. Employment Information</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {renderField('Employment Status', previewData.kyc_data.employmentInfo?.employmentStatus)}
                      {renderField('Employer Name', previewData.kyc_data.employmentInfo?.employerName)}
                      {renderField('Occupation', previewData.kyc_data.employmentInfo?.occupation)}
                      {renderField('Industry', previewData.kyc_data.employmentInfo?.employmentIndustry)}
                      {renderField('Years Employed', previewData.kyc_data.employmentInfo?.yearsEmployed)}
                      {renderField('Annual Income', previewData.kyc_data.employmentInfo?.annualIncome)}
                      {renderField('Income Currency', previewData.kyc_data.employmentInfo?.incomeCurrency)}
                    </dl>
                  </div>

                  {/* 5. Tax Information (TaxInfoCreate) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">5. Tax Information</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {renderField('Tax ID', previewData.kyc_data.taxInfo?.taxId)}
                      {renderField('Tax Residence', previewData.kyc_data.taxInfo?.taxResidence)}
                      {renderField('US Person', previewData.kyc_data.taxInfo?.usPerson)}
                      {renderField('FATCA Status', previewData.kyc_data.taxInfo?.fatcaStatus)}
                    </dl>
                  </div>

                  {/* 6. PEP & Insider Status (PEPStatusCreate - includes insider per kyc-service model) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      6. PEP & Insider Status
                    </h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {renderField('Classification', previewData.kyc_data.pepStatus?.pepClassification || previewData.kyc_data.pepStatus?.classification)}
                      {renderField('Is PEP', previewData.kyc_data.pepStatus?.isPep || previewData.kyc_data.pepStatus?.isPEP)}
                      {renderField('Is RCA', previewData.kyc_data.pepStatus?.isRca || previewData.kyc_data.pepStatus?.isRCA)}
                      {renderField('PEP Type', previewData.kyc_data.pepStatus?.pepType)}
                      {renderField('PEP Role', previewData.kyc_data.pepStatus?.pepRole)}
                      {renderField('PEP Relation', previewData.kyc_data.pepStatus?.pepRelation)}
                      {renderField('Positions', previewData.kyc_data.pepStatus?.pepPositions || previewData.kyc_data.pepStatus?.positions)}
                      {renderField('Countries', previewData.kyc_data.pepStatus?.pepCountries || previewData.kyc_data.pepStatus?.countries)}
                      {renderField('Start Date', previewData.kyc_data.pepStatus?.pepStartDate || previewData.kyc_data.pepStatus?.startDate)}
                      {renderField('End Date', previewData.kyc_data.pepStatus?.pepEndDate || previewData.kyc_data.pepStatus?.endDate)}
                      {renderField('Years in Position', previewData.kyc_data.pepStatus?.pepYearsInPosition || previewData.kyc_data.pepStatus?.yearsInPosition)}
                      {renderField('Years Since Exit', previewData.kyc_data.pepStatus?.pepYearsSinceExit || previewData.kyc_data.pepStatus?.yearsSinceExit)}
                      {renderField('Risk Level', previewData.kyc_data.pepStatus?.pepRiskLevel)}
                      {renderField('Enhanced Due Diligence', previewData.kyc_data.pepStatus?.enhancedDueDiligence)}
                      {renderField('Monitoring Frequency', previewData.kyc_data.pepStatus?.monitoringFrequency)}
                      {renderField('Is Insider', previewData.kyc_data.pepStatus?.isInsider)}
                      {renderField('Board Position ISINs', previewData.kyc_data.pepStatus?.boardPositionIsins || previewData.kyc_data.pepStatus?.insiderSecurities)}
                      {renderField('Insider Companies', previewData.kyc_data.pepStatus?.insiderCompanyNames)}
                    </dl>
                  </div>

                  {/* 7. Personal Background (PersonalBackgroundCreate) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">7. Personal Background</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {renderField('Education Level', previewData.kyc_data.personalBackground?.educationLevel)}
                      {renderField('Institutions Attended', previewData.kyc_data.personalBackground?.institutionsAttended)}
                      {renderField('Professional Certifications', previewData.kyc_data.personalBackground?.professionalCertifications)}
                      {renderField('Hobbies & Interests', previewData.kyc_data.personalBackground?.hobbiesInterests)}
                      {renderField('Family Composition', previewData.kyc_data.personalBackground?.familyComposition)}
                      {renderField('Dependents Count', previewData.kyc_data.personalBackground?.dependentsCount)}
                      {renderField('Spouse Employment Status', previewData.kyc_data.personalBackground?.spouseEmploymentStatus || previewData.kyc_data.personalBackground?.spousePartnerEmploymentStatus)}
                    </dl>
                  </div>

                  {/* 8. Wealth Summary (profile-level fields from KYCProfileCreate) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">8. Wealth Summary</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {renderField('Total Net Worth', previewData.kyc_data.wealthSummary?.netWorth)}
                      {renderField('Liquid Net Worth', previewData.kyc_data.wealthSummary?.liquidNetWorth)}
                      {renderField('Net Worth Currency', previewData.kyc_data.wealthSummary?.netWorthCurrency)}
                      {renderField('Source of Wealth', previewData.kyc_data.wealthSummary?.sourceOfWealth)}
                      {renderField('Source of Funds', previewData.kyc_data.wealthSummary?.sourceOfFunds)}
                      {renderField('Primary Wealth Source', previewData.kyc_data.wealthSummary?.primaryWealthSource)}
                      {renderField('Wealth Narrative', previewData.kyc_data.wealthSummary?.wealthNarrative)}
                      {/* Additional wealth info from additionalWealthInfo section */}
                      {renderField('Inheritance Expected', previewData.kyc_data.additionalWealthInfo?.inheritanceExpected)}
                      {renderField('Inheritance Details', previewData.kyc_data.additionalWealthInfo?.inheritanceDetails)}
                      {renderField('Unusual Transactions', previewData.kyc_data.additionalWealthInfo?.unusualTransactions)}
                    </dl>
                  </div>

                  {/* 9. Asset Breakdown */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">9. Asset Breakdown</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {renderField('Cash %', previewData.kyc_data.assetBreakdown?.assetBreakdownCash)}
                      {renderField('Securities %', previewData.kyc_data.assetBreakdown?.assetBreakdownSecurities)}
                      {renderField('Real Estate %', previewData.kyc_data.assetBreakdown?.assetBreakdownRealEstate)}
                      {renderField('Business Equity %', previewData.kyc_data.assetBreakdown?.assetBreakdownBusinessEquity)}
                      {renderField('Commodities %', previewData.kyc_data.assetBreakdown?.assetBreakdownCommodities)}
                      {renderField('Crypto %', previewData.kyc_data.assetBreakdown?.assetBreakdownCrypto)}
                      {renderField('Other %', previewData.kyc_data.assetBreakdown?.assetBreakdownOther)}
                    </dl>
                  </div>

                  {/* 10. Future Income Expectations (Profile-level fields) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">10. Future Income Expectations</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {renderField('Expects Income Change', previewData.kyc_data.incomeExpectations?.expectedIncomeChange)}
                      {renderField('Change Details', previewData.kyc_data.incomeExpectations?.expectedChangeDetails)}
                      {renderField('Retirement Income', previewData.kyc_data.incomeExpectations?.retirementIncome)}
                      {renderField('Retirement Details', previewData.kyc_data.incomeExpectations?.retirementIncomeDetails)}
                      {renderField('Other Financial Support', previewData.kyc_data.incomeExpectations?.otherFinancialSupport)}
                      {renderField('Support Details', previewData.kyc_data.incomeExpectations?.otherSupportDetails)}
                    </dl>
                  </div>

                  {/* 11. Wealth Sources (WealthSourceCreate[]) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">11. Wealth Sources</h4>
                    {previewData.kyc_data.wealthSources && previewData.kyc_data.wealthSources.length > 0 ? (
                      <div className="space-y-2">
                        {previewData.kyc_data.wealthSources.map((source: any, index: number) => (
                          <div key={index} className="border border-gray-100 rounded p-2 bg-gray-50">
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                              {renderField('Source Type', source.sourceType)}
                              {renderField('Description', source.sourceDescription)}
                              {renderField('Percentage', source.percentage)}
                              {renderField('Acquisition Period', source.acquisitionPeriod)}
                              {renderField('Asset Type', source.assetType)}
                              {renderField('Asset Value', source.assetValue)}
                              {renderField('Asset Location', source.assetLocation)}
                              {renderField('Liquidity Level', source.liquidityLevel)}
                              {renderField('Risk Level', source.sourceRiskLevel)}
                              {renderField('Documented', source.documented)}
                            </dl>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No wealth sources extracted</p>
                    )}
                  </div>

                  {/* 12. Income Sources (IncomeSourceCreate[]) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">12. Income Sources</h4>
                    {previewData.kyc_data.incomeSources && previewData.kyc_data.incomeSources.length > 0 ? (
                      <div className="space-y-2">
                        {previewData.kyc_data.incomeSources.map((source: any, index: number) => (
                          <div key={index} className="border border-gray-100 rounded p-2 bg-gray-50">
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                              {renderField('Income Type', source.incomeType)}
                              {renderField('Description', source.incomeDescription)}
                              {renderField('Annual Amount', source.annualAmount)}
                              {renderField('Currency', source.currency || source.incomeCurrency)}
                              {renderField('Frequency', source.frequency)}
                              {renderField('Source Name', source.sourceName)}
                              {renderField('Source Country', source.sourceCountry)}
                              {renderField('Verified', source.verified)}
                              {renderField('Tax Paid', source.taxPaid)}
                              {renderField('Tax Jurisdiction', source.taxJurisdiction)}
                              {renderField('Stability Rating', source.stabilityRating)}
                            </dl>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No income sources extracted</p>
                    )}
                  </div>

                  {/* 13. Adverse Media */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">13. Adverse Media</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {renderField('Has Adverse Media', previewData.kyc_data.adverseMedia?.hasAdverseMedia)}
                      {renderField('Screening Date', previewData.kyc_data.adverseMedia?.screeningDate)}
                      {renderField('Screening Tool', previewData.kyc_data.adverseMedia?.screeningTool)}
                      {renderField('Severity Level', previewData.kyc_data.adverseMedia?.severityLevel)}
                      {renderField('Reviewed', previewData.kyc_data.adverseMedia?.reviewed)}
                      {renderField('False Positive', previewData.kyc_data.adverseMedia?.falsePositive)}
                    </dl>
                  </div>

                  {/* 14. Business Activities */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">14. Business Activities</h4>
                    {previewData.kyc_data.businessActivities && previewData.kyc_data.businessActivities.length > 0 ? (
                      <div className="space-y-2">
                        {previewData.kyc_data.businessActivities.map((activity: any, index: number) => (
                          <div key={index} className="border border-gray-100 rounded p-2 bg-gray-50">
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                              {renderField('Business Name', activity.businessName)}
                              {renderField('Business Type', activity.businessType)}
                              {renderField('Registration Number', activity.registrationNumber)}
                              {renderField('Registration Country', activity.registrationCountry)}
                              {renderField('Primary Industry', activity.primaryIndustry)}
                              {renderField('Secondary Industries', activity.secondaryIndustries)}
                              {renderField('Industry Risk Level', activity.industryRiskLevel)}
                              {renderField('Ownership %', activity.ownershipPercentage)}
                              {renderField('Ownership Type', activity.ownershipType)}
                              {renderField('Countries of Operation', activity.countriesOfOperation)}
                              {renderField('High-Risk Countries', activity.highRiskCountries)}
                              {renderField('Years in Business', activity.yearsInBusiness)}
                              {renderField('Number of Employees', activity.numberOfEmployees)}
                              {renderField('Annual Revenue', activity.annualRevenue)}
                              {renderField('Revenue Currency', activity.revenueCurrency)}
                              {renderField('Cash Intensive', activity.cashIntensive)}
                              {renderField('Cross-Border Transactions', activity.crossBorderTransactions)}
                              {renderField('Complex Ownership', activity.complexOwnership)}
                            </dl>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No business activities extracted</p>
                    )}
                  </div>
                </div>
              )}

              {/* Risk Profile Tab */}
              {activeTab === 'risk' && (databaseRiskProfile || previewData.risk_profile_data) && (
                <div className="p-6 space-y-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {databaseRiskProfile ? 'Risk Profile from Database' : 'Extracted Risk Profile'}
                    </h3>
                    {databaseRiskProfile ? (
                      <span className="text-xs text-green-600 bg-green-50 px-3 py-1 rounded-full">
                        From Database
                      </span>
                    ) : previewData.risk_profile_data?._inference && (
                      <span className="text-xs text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                        {previewData.risk_profile_data._inference.confidence}
                      </span>
                    )}
                  </div>

                  {/* Show Database Risk Profile if available */}
                  {databaseRiskProfile ? (
                    <>
                      {/* Database Risk Profile - Section by Section */}
                      {Object.keys(databaseRiskProfile)
                        .filter(key => key.startsWith('section'))
                        .sort()
                        .map(sectionKey => {
                          const sectionData = databaseRiskProfile[sectionKey];
                          if (!sectionData || Object.keys(sectionData).length === 0) return null;

                          const sectionNumber = sectionKey.replace('section', '');
                          const sectionTitles: Record<string, string> = {
                            '1': 'Personal Information',
                            '2': 'Financial Situation',
                            '3': 'Investment Experience',
                            '4': 'Investment Objectives',
                            '5': 'Risk Tolerance',
                            '6': 'Time Horizon',
                            '7': 'Liquidity Needs',
                            '8': 'Tax Considerations',
                            '9': 'Legal & Regulatory',
                            '10': 'Other Considerations'
                          };

                          return (
                            <div key={sectionKey} className="bg-white border border-gray-200 rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 mb-3">
                                Section {sectionNumber}: {sectionTitles[sectionNumber] || 'Additional Info'}
                              </h4>
                              <dl className="space-y-1">
                                {Object.entries(sectionData).map(([key, value]) => {
                                  if (key.startsWith('_')) return null; // Skip metadata
                                  const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                  return renderField(label, value);
                                })}
                              </dl>
                            </div>
                          );
                        })}

                      {/* Metadata if available */}
                      {databaseRiskProfile._metadata && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h4 className="font-medium text-gray-700 mb-3 text-sm">Extraction Metadata</h4>
                          <dl className="space-y-1">
                            {Object.entries(databaseRiskProfile._metadata).map(([key, value]) => {
                              const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                              return renderField(label, value);
                            })}
                          </dl>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* TASK-225: Render risk profile data - supports both flat and section-based structure */}
                      {/* Metadata Notice */}
                      {previewData.risk_profile_data._metadata && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-blue-900">
                                RAG Extraction Result
                              </p>
                              <p className="text-xs text-blue-700 mt-1">
                                {previewData.risk_profile_data._metadata.fields_extracted} of {previewData.risk_profile_data._metadata.fields_total} fields extracted
                                (Confidence: {((previewData.risk_profile_data._metadata.extraction_confidence || 0) * 100).toFixed(0)}%)
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* TASK-225: Section-based structure (section1-section10) */}
                      {previewData.risk_profile_data.section1 && (
                        <>
                          {/* Section 1: Personal Information */}
                          {Object.keys(previewData.risk_profile_data.section1 || {}).length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 mb-3">1. Personal Information</h4>
                              <dl className="space-y-1">
                                {Object.entries(previewData.risk_profile_data.section1).map(([key, val]: [string, any]) =>
                                  renderField(key.replace(/([A-Z])/g, ' $1').trim(), typeof val === 'object' && val?.value !== undefined ? val.value : val)
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Section 2: Knowledge & Experience */}
                          {Object.keys(previewData.risk_profile_data.section2 || {}).length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 mb-3">2. Knowledge & Experience</h4>
                              <dl className="space-y-1">
                                {Object.entries(previewData.risk_profile_data.section2).map(([key, val]: [string, any]) =>
                                  renderField(key.replace(/([A-Z])/g, ' $1').trim(), typeof val === 'object' && val?.value !== undefined ? val.value : val)
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Section 3: Financial Situation */}
                          {Object.keys(previewData.risk_profile_data.section3 || {}).length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 mb-3">3. Financial Situation</h4>
                              <dl className="space-y-1">
                                {Object.entries(previewData.risk_profile_data.section3).map(([key, val]: [string, any]) => {
                                  const value = typeof val === 'object' && val?.value !== undefined ? val.value : val;
                                  // Format currency values
                                  const displayValue = typeof value === 'object' && value?.amount
                                    ? `${value.currency || 'CHF'} ${value.amount.toLocaleString()}`
                                    : value;
                                  return renderField(key.replace(/([A-Z])/g, ' $1').trim(), displayValue);
                                })}
                              </dl>
                            </div>
                          )}

                          {/* Section 4: Investment Objectives */}
                          {Object.keys(previewData.risk_profile_data.section4 || {}).length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 mb-3">4. Investment Objectives</h4>
                              <dl className="space-y-1">
                                {Object.entries(previewData.risk_profile_data.section4).map(([key, val]: [string, any]) =>
                                  renderField(key.replace(/([A-Z])/g, ' $1').trim(), typeof val === 'object' && val?.value !== undefined ? val.value : val)
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Section 5: Risk Tolerance */}
                          {Object.keys(previewData.risk_profile_data.section5 || {}).length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 mb-3">5. Risk Tolerance</h4>
                              <dl className="space-y-1">
                                {Object.entries(previewData.risk_profile_data.section5).map(([key, val]: [string, any]) =>
                                  renderField(key.replace(/([A-Z])/g, ' $1').trim(), typeof val === 'object' && val?.value !== undefined ? val.value : val)
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Section 6: Determined Risk Profile */}
                          {Object.keys(previewData.risk_profile_data.section6 || {}).length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 mb-3">6. Determined Risk Profile</h4>
                              <dl className="space-y-1">
                                {Object.entries(previewData.risk_profile_data.section6).map(([key, val]: [string, any]) =>
                                  renderField(key.replace(/([A-Z])/g, ' $1').trim(), typeof val === 'object' && val?.value !== undefined ? val.value : val)
                                )}
                              </dl>
                            </div>
                          )}

                          {/* Section 7: Investment Strategy */}
                          {Object.keys(previewData.risk_profile_data.section7 || {}).length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 mb-3">7. Investment Strategy</h4>
                              <dl className="space-y-1">
                                {Object.entries(previewData.risk_profile_data.section7).map(([key, val]: [string, any]) => {
                                  const value = typeof val === 'object' && val?.value !== undefined ? val.value : val;
                                  const displayValue = typeof value === 'number' && key.includes('Allocation') ? `${value}%` : value;
                                  return renderField(key.replace(/([A-Z])/g, ' $1').trim(), displayValue);
                                })}
                              </dl>
                            </div>
                          )}
                        </>
                      )}

                      {/* Legacy flat structure fallback */}
                      {!previewData.risk_profile_data.section1 && (
                        <>
                          {/* Inference Notice */}
                          {previewData.risk_profile_data._inference && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                              <div className="flex items-start gap-2">
                                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-blue-900">
                                    {previewData.risk_profile_data._inference.method}
                                  </p>
                                  <p className="text-xs text-blue-700 mt-1">
                                    {previewData.risk_profile_data._inference.note}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Investment Profile */}
                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-3">Investment Profile</h4>
                            <dl className="space-y-1">
                              {renderField('Investment Horizon', previewData.risk_profile_data.investmentHorizon)}
                              {renderField('Investment Experience', previewData.risk_profile_data.investmentExperience)}
                              {renderField('Financial Knowledge', previewData.risk_profile_data.financialKnowledge)}
                              {renderField('Risk Tolerance', previewData.risk_profile_data.riskTolerance)}
                              {renderField('Risk Capacity', previewData.risk_profile_data.riskCapacity)}
                            </dl>
                          </div>

                          {/* All Other Fields */}
                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-3">Additional Information</h4>
                            <dl className="space-y-1">
                              {Object.keys(previewData.risk_profile_data).map(key => {
                                const skipFields = ['_inference', '_metadata', 'investmentHorizon', 'investmentExperience',
                                  'financialKnowledge', 'riskTolerance', 'riskCapacity'];
                                if (skipFields.includes(key)) return null;
                                return renderField(
                                  key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                                  previewData.risk_profile_data[key]
                                );
                              })}
                            </dl>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Portfolio Tab */}
              {activeTab === 'portfolio' && previewData.sample_rows && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Extracted Portfolio Positions
                    </h3>
                    <span className="text-sm text-gray-600">
                      {previewData.total_rows || previewData.sample_rows.length} positions
                    </span>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex gap-2">
                      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <div className="text-sm text-blue-900">
                        <p className="font-medium">Review portfolio positions before approval</p>
                        <p className="text-blue-800 mt-1">
                          Verify ISINs, quantities, and values are correct. Data will be imported to the portfolio management system.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {Object.keys(previewData.sample_rows[0] || {}).map((key) => (
                            <th key={key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50">
                              {key.replace(/_/g, ' ')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {previewData.sample_rows.map((row: any, index: number) => (
                          <tr key={index} className="hover:bg-gray-50">
                            {Object.entries(row).map(([key, value]: [string, any], cellIndex: number) => {
                              // Format numbers with currency/percentage
                              let displayValue = String(value || '');
                              if (typeof value === 'number') {
                                if (key.includes('value') || key.includes('price') || key.includes('amount')) {
                                  displayValue = new Intl.NumberFormat('en-CH', {
                                    style: 'currency',
                                    currency: 'CHF'
                                  }).format(value);
                                } else if (key.includes('percent') || key.includes('weight')) {
                                  displayValue = `${value.toFixed(2)}%`;
                                } else {
                                  displayValue = value.toLocaleString('en-CH');
                                }
                              }
                              return (
                                <td key={cellIndex} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                  {displayValue}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Transactions Tab */}
              {activeTab === 'transactions' && previewData.sample_rows && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Extracted Transactions
                    </h3>
                    <span className="text-sm text-gray-600">
                      {previewData.total_rows || previewData.sample_rows.length} transactions
                    </span>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex gap-2">
                      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <div className="text-sm text-blue-900">
                        <p className="font-medium">Review transaction history before approval</p>
                        <p className="text-blue-800 mt-1">
                          Verify dates, transaction types, and amounts. Data will be imported to the transaction history.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {Object.keys(previewData.sample_rows[0] || {}).map((key) => (
                            <th key={key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50">
                              {key.replace(/_/g, ' ')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {previewData.sample_rows.map((row: any, index: number) => (
                          <tr key={index} className="hover:bg-gray-50">
                            {Object.entries(row).map(([key, value]: [string, any], cellIndex: number) => {
                              // Format values based on column type
                              let displayValue = String(value || '');
                              let cellClass = "px-4 py-3 text-sm text-gray-900 whitespace-nowrap";

                              if (typeof value === 'number') {
                                if (key.includes('amount') || key.includes('price') || key.includes('fee') || key.includes('tax')) {
                                  displayValue = new Intl.NumberFormat('en-CH', {
                                    style: 'currency',
                                    currency: 'CHF'
                                  }).format(value);
                                } else if (key.includes('quantity') || key.includes('shares')) {
                                  displayValue = value.toLocaleString('en-CH');
                                }
                              } else if (key.includes('type') && value) {
                                // Color-code transaction types
                                const type = String(value).toLowerCase();
                                if (type.includes('buy')) {
                                  cellClass += " text-green-700 font-medium";
                                } else if (type.includes('sell')) {
                                  cellClass += " text-red-700 font-medium";
                                } else if (type.includes('dividend') || type.includes('interest')) {
                                  cellClass += " text-blue-700 font-medium";
                                }
                              }

                              return (
                                <td key={cellIndex} className={cellClass}>
                                  {displayValue}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">No preview data available</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 flex flex-wrap gap-2">
              {previewData?.kyc_data && (
                <span className="text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                  ✓ KYC Data Extracted
                </span>
              )}
              {/* TASK-225: Show Risk Profile badge when risk_profile_data exists */}
              {(databaseRiskProfile || previewData?.risk_profile_data) && (
                <span className="text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                  ✓ Risk Profile
                </span>
              )}
              {previewData?.sample_rows && previewData.sample_rows.length > 0 && (() => {
                const dataType = detectDataType(previewData.sample_rows);
                const label = dataType === 'transactions' ? 'Transaction Data' : 'Portfolio Data';
                return (
                  <span className="text-purple-600 font-medium bg-purple-50 px-2 py-1 rounded">
                    ✓ {label} ({previewData.sample_rows.length})
                  </span>
                );
              })()}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={approving || loading || !!error}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {approving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Approving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Approve & Import All Data
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtractionPreviewModal;
